import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { InterPoint, Logger } from '@saviynt/common';
import { Icon, SidePanel, Typography } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { httpService } from '../../../http-service/httpService';
import {
  setConnectedTileId,
  setIsSidePanelVisible,
  setSidePanelMetaData,
} from '../../../redux/certlist/action';
import { URL_SIDE_PANEL_PROGRESS_API } from '../../../redux/certlist/urls';
import {
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_SIZE_TABLET_LARGE,
  SCREEN_SIZE_TABLET_SMALL,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import Carousel from '../../Common/Carousel';
import CertsListAction from '../CertsListAction';
import {
  CAMP_TYPE_AO,
  CAMP_TYPE_EO,
  CAMP_TYPE_SA,
  CAMP_TYPE_UM,
} from '../CertsListTile/constant';
import { ACTION_BUTTON_FOOTER_KIND, CERTS_LIST_EXPIRED_TAB } from '../constant';

import CertsListSidePanelBody from './CertsListSidePanelBody';

import './CertsListSidePanel.css';

function CertsListSidePanel({ dataTestId }) {
  const dispatch = useDispatch();
  const {
    global: { mediaQueryValue },
    core: { isAdminLogin },
    sidePanel: {
      isSidePanelVisible,
      connectedTileId,
      sidePanelMetaData,
      siblingTileId,
      tempActionState, // TODO: This is the temp state till the time Carousel is updated for BreadCrumb
    },
  } = useSelector((state) => state?.certifications?.list);
  const { isMenuPinned } = useSelector((state) => state?.user);

  const certsListSidePanelRef = useRef();
  const [headerProps, setHeaderProps] = useState({
    title: '',
    description: '',
    icon: null,
  });

  const tabletLargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_LARGE
  );
  const tabletSmallQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_SMALL
  );
  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isTabletLargeWidth = tabletLargeQuery.value <= mediaQueryValue;
  const isTabletSmallWidth = tabletSmallQuery.value <= mediaQueryValue;
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  const sidePanelClasses = classnames(
    'CertsListSidePanel',
    isMenuPinned && 'CertsListSidePanel--isMenuPinned'
  );

  const setSidePanelDetails = async () => {
    let latestProgressPercentage = 0;
    const { id } = sidePanelMetaData;

    if (id) {
      const decisionBody = {
        certkey: String(id),
        group_by: 'decision',
      };

      // add this implementation only when login user is not an admin and EO campaign (CR-19418) , Need optimization
      if (
        (!isAdminLogin &&
          (sidePanelMetaData?.campaignType === CAMP_TYPE_UM ||
            sidePanelMetaData?.campaignType === CAMP_TYPE_SA ||
            sidePanelMetaData?.campaignType === CAMP_TYPE_AO)) ||
        sidePanelMetaData?.campaignType === CAMP_TYPE_EO
      ) {
        try {
          const response = await httpService.post(
            URL_SIDE_PANEL_PROGRESS_API,
            decisionBody
          );

          if (response?.data) {
            const { Summary } = response.data;

            latestProgressPercentage = `${Summary.Progress}%`;
          }
        } catch (e) {
          Logger.error('setSidePanelDetails Error', e);
        }
      }

      const description = (
        <>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color='--color-foreground-secondary-bold'>
            {sidePanelMetaData.campaignType}
          </Typography>
          <InterPoint />
          <Icon
            kind={sidePanelMetaData.statusIconKind}
            size='xSmall'
            color={sidePanelMetaData.statusIconColor}
          />
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={sidePanelMetaData.statusTextColor}>
            {sidePanelMetaData.statusText}
          </Typography>
          {sidePanelMetaData?.tab === CERTS_LIST_EXPIRED_TAB ? (
            <div
              className='CertsListSidePanel-headerExpiredTooltip'
              data-message='Some access was automatically revoked after the certification due date.'>
              <Icon
                kind='AlertInfoFilledSeparateColors'
                color='--color-foreground-info-medium'
                size='xSmall'
              />
            </div>
          ) : (
            <Typography
              kind='body3-bold'
              desktop='body3-bold-d'
              mobile='body3-bold-d'
              color='--color-foreground-neutral-bold'>
              {latestProgressPercentage || sidePanelMetaData.progress}
            </Typography>
          )}
        </>
      );

      setHeaderProps((prev) => ({
        ...prev,
        title: sidePanelMetaData.title,
        description,
      }));
    }
  };

  useEffect(() => {
    if (sidePanelMetaData) {
      setSidePanelDetails();
    }
  }, [sidePanelMetaData]);

  const handleCloseSidePanel = () => {
    dispatch(setIsSidePanelVisible(false));
  };

  const handleNext = () => {
    if (connectedTileId !== siblingTileId?.next?.id) {
      dispatch(setConnectedTileId(siblingTileId?.next?.id));
      dispatch(setSidePanelMetaData(siblingTileId?.next));
    }
  };

  const handlePrev = () => {
    if (connectedTileId !== siblingTileId?.prev?.id) {
      dispatch(setConnectedTileId(siblingTileId?.prev?.id));
      dispatch(setSidePanelMetaData(siblingTileId?.prev));
    }
  };

  const getFooterActionBar = () => (
    <CertsListAction
      kind={ACTION_BUTTON_FOOTER_KIND.footer}
      certKey={sidePanelMetaData?.id}
      actionsMap={sidePanelMetaData?.actions}
      isPrimaryCertifierActive={sidePanelMetaData?.isPrimaryCertifierActive}
      tileCampaignKey={sidePanelMetaData?.tileCampaignKey}
      campaignType={sidePanelMetaData?.campaignType}
      tileStatusLabel={sidePanelMetaData?.tileStatusLabel}
      newCertUX={sidePanelMetaData?.newCertUX}
      certDetailsUrl={sidePanelMetaData?.certDetailsUrl}
      certificationName={sidePanelMetaData?.title}
    />
  );

  if (isMobileXlargeWidth) {
    return ReactDOM.createPortal(
      <Carousel
        isOpen={isSidePanelVisible}
        onClose={handleCloseSidePanel}
        header={{
          title: headerProps.title,
          description: headerProps.description,
        }}
        layout={
          <CertsListSidePanelBody
            handleCloseSidePanel={handleCloseSidePanel}
            tabGroupRef={certsListSidePanelRef}
          />
        }
        footer={getFooterActionBar()}
        className={sidePanelClasses}
        headerClassName='CertsListSidePanel-header'
        layoutClassName='CertsListSidePanel-layout'
        isPrevDisabled={Boolean(!siblingTileId?.prev)}
        isNextDisabled={Boolean(!siblingTileId?.next)}
        onClickNext={handleNext}
        onClickPrev={handlePrev}
        isInBackground={tempActionState}
        dataTestId={dataTestId}
      />,
      document.body
    );
  }

  return (
    <SidePanel
      isOpen={isSidePanelVisible}
      onClose={handleCloseSidePanel}
      className={sidePanelClasses}
      headerClassName='CertsListSidePanel-header'
      layoutClassName='CertsListSidePanel-layout'
      header={{
        title: headerProps.title,
        description: headerProps.description,
      }}
      layout={
        <CertsListSidePanelBody
          handleCloseSidePanel={handleCloseSidePanel}
          tabGroupRef={certsListSidePanelRef}
        />
      }
      footer={getFooterActionBar()}
      isSidePanelNavigatingList
      handlePrevious={handlePrev}
      handleNext={handleNext}
      isPreviousDisabled={Boolean(!siblingTileId?.prev)}
      isNextDisabled={Boolean(!siblingTileId?.next)}
      isTabletWidth={(isMenuPinned && isTabletLargeWidth) || isTabletSmallWidth}
      isMobileWidth={isMobileXlargeWidth}
      dataTestId={dataTestId}
    />
  );
}

CertsListSidePanel.propTypes = {
  dataTestId: PropTypes.string,
};

CertsListSidePanel.defaultProps = {
  dataTestId: 'CertListSidePanel-dataTestId',
};

export default CertsListSidePanel;
